import { useId } from 'react';
import type { InputHTMLAttributes } from 'react';

import focus from './focus.module.css';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string;
  /** Shown under the field when there is no error. */
  readonly hint?: string;
  /** Shown instead of the hint, and marks the field invalid to a screen reader. */
  readonly error?: string;
  /** Right-aligned tabular figures, for anything numeric. */
  readonly numeric?: boolean;
}

/**
 * A labelled text field. The label is required rather than optional: a placeholder is not a label — it
 * disappears the moment someone types, which is exactly when they need it.
 */
export function Input({
  label,
  hint,
  error,
  numeric = false,
  className,
  ...rest
}: InputProps): React.JSX.Element {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  const classes = [styles.input, focus.focusable, numeric ? styles.numeric : '', error ? styles.invalid : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={classes}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? messageId : undefined}
        inputMode={numeric ? 'decimal' : undefined}
        {...rest}
      />
      {message ? (
        <span id={messageId} className={[styles.message, error ? styles.error : ''].filter(Boolean).join(' ')}>
          {message}
        </span>
      ) : null}
    </div>
  );
}
